/**
 * @description Display helpers for time entries (punch vs typed PTO).
 *
 * Full-day PTO rows are stored as UTC midnight + hours_requested duration.
 * Rendering those with local toLocale* shifts the calendar day (e.g. Jul 28
 * 00:00Z → Jul 27 evening in US). Application standard for calendar dates is
 * the same as invoiceHelpers.parseDateSafely: use the YYYY-MM-DD prefix.
 */

export function isPtoEntry(entry) {
  return entry?.entryKind === 'pto' || entry?.entry_kind === 'pto';
}

/**
 * Calendar date from an ISO timestamp without local TZ day-shift.
 * @param {string|Date} iso
 * @param {Intl.DateTimeFormatOptions} [options]
 * @returns {string}
 */
export function formatEntryCalendarDate(iso, options = { month: 'short', day: 'numeric', year: 'numeric' }) {
  if (!iso) return '—';
  const raw = iso instanceof Date ? iso.toISOString() : String(iso);
  const [year, month, day] = raw.slice(0, 10).split('-').map(Number);
  if (!year || !month || !day) return '—';
  return new Date(year, month - 1, day).toLocaleDateString('en-US', options);
}

/**
 * Wall-clock time for punches; PTO shows em dash (duration carries the hours).
 * @param {object} entry
 * @param {'in'|'out'} which
 * @returns {string}
 */
export function formatEntryClockTime(entry, which = 'in') {
  if (isPtoEntry(entry)) return '—';
  const iso = which === 'out' ? entry?.clockOutAt : entry?.clockInAt;
  if (!iso) return which === 'out' ? 'In Progress' : '—';
  return new Date(iso).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
}

/**
 * Human label for entry kind / channel badge.
 * @param {object} entry
 * @returns {string}
 */
export function formatEntryKindLabel(entry) {
  if (isPtoEntry(entry)) return 'pto';
  return entry?.entryType || entry?.entry_type || 'clock';
}

/**
 * Badge classes for kind label.
 * @param {object} entry
 * @returns {string}
 */
export function entryKindBadgeClass(entry) {
  if (isPtoEntry(entry)) return 'bg-teal-50 text-teal-800';
  const type = entry?.entryType || entry?.entry_type;
  if (type === 'clock') return 'bg-blue-50 text-blue-700';
  if (type === 'manual') return 'bg-purple-50 text-purple-700';
  return 'bg-orange-50 text-orange-700';
}
